import { FiCornerDownLeft, FiLink, FiPhone } from "react-icons/fi";
import {
  doubleTick,
  phoneIcon,
  verticalDot,
  videoIcon,
  whtsappBg,
  whtsappIcon,
} from "../../asset";

interface Button {
  id: string;
  type: "Quick Reply" | "URL" | "Call to Action";
  text: string;
  url?: string;
  phoneNumber?: string;
}

interface WhatsappPreviewProps {
  templateData: any;
  buttons: Button[];
  mediaPreviewUrl?: string;  // local blob URL for preview
  mediaFile?: File | null;   // for showing document filename
  headerParams?: string[];
  bodyParams?: string[];
  headerMediaUploading?: boolean;
}

export default function WhatsappPreview({
  templateData,
  buttons,
  mediaPreviewUrl,
  mediaFile,
  headerParams = [],
  bodyParams = [],
  headerMediaUploading = false
}: WhatsappPreviewProps) {

  const fillVariables = (text: string, params: string[]) => {
    if (!text) return text;
    return text.replace(/\{\{(\d+)\}\}/g, (match, num) => {
      const index = Number(num) - 1;
      const value = params[index];
      return value && value.trim() ? value : match;
    });
  };

  const renderHeader = () => {
    if (!templateData?.headerType) return null;

    const headerType = templateData.headerType.toUpperCase();

    switch (headerType) {
      // case "TEXT":
      //   return (
      //     <div className="font-bold text-sm mb-2">
      //       {templateData.headerText || "Header text here"}
      //     </div>
      //   );
      case "TEXT":
        return (
          <div className="font-bold text-sm mb-2">
            {fillVariables(templateData.headerText, headerParams) || "Header text here"}
          </div>
        );
      case "IMAGE":
        return (
          <div className="mb-2 rounded-lg overflow-hidden bg-gray-200 h-32 relative">
            {mediaPreviewUrl || templateData?.headerImageUrl ? (
              <img
                src={mediaPreviewUrl || templateData?.headerImageUrl}
                alt="Header"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-xs text-gray-500">📷 Image Header</span>
              </div>
            )}
            {headerMediaUploading && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        );

      case "VIDEO":
        return (
          <div className="mb-2 rounded-lg overflow-hidden bg-gray-200 h-32 relative">
            {mediaPreviewUrl ? (
              <video
                src={mediaPreviewUrl}
                className="w-full h-full object-cover"
                muted
                playsInline
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-xs text-gray-500">🎥 Video Header</span>
              </div>
            )}
            {headerMediaUploading && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        );

      case "DOCUMENT":
        return (
          <div className="mb-2 p-3 rounded-lg bg-gray-100 flex items-center gap-2 relative">
            <span className="text-xl">📄</span>
            <span className="text-xs text-gray-600 truncate">
              {mediaFile?.name || "Document.pdf"}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  const callToActionButtons = buttons.filter(
    (btn) => btn.type === "URL" || btn.type === "Call to Action"
  );
  const quickReplyButtons = buttons.filter((btn) => btn.type === "Quick Reply");

  const renderCallToActionButtons = () => {
    if (callToActionButtons.length === 0) return null;

    return (
      <div className="mt-2 pt-2 border-t border-gray-300 space-y-1">
        {callToActionButtons.map((button) => {
          const buttonIcon =
            button.type === "URL" ? (
              <FiLink size={14} />
            ) : (
              <FiPhone size={14} />
            );
          return (
            <button
              key={button.id}
              className="w-full py-2 text-[#00A5F4] text-sm font-medium text-center hover:bg-gray-50 transition flex items-center justify-center gap-1"
            >
              <span className="text-base">{buttonIcon}</span>
              <span>{button.text || "Button"}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderQuickReplyButtons = () => {
    if (quickReplyButtons.length === 0) return null;

    return (
      <div className="mt-2 w-full space-y-2">
        {quickReplyButtons.map((button) => (
          <button
            key={button.id}
            className="w-full bg-white text-[#00A5F4] text-sm font-medium py-2 px-4 rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition flex items-center justify-center gap-1"
          >
            <FiCornerDownLeft size={14} />
            <span>{button.text || "Quick Reply"}</span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-lg border overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-3">
          <img src={whtsappIcon} className="w-8 h-8" alt="WhatsApp" />
          <span className="text-sm font-semibold text-black">
            Whatsapp Business
          </span>
        </div>
        <div className="flex gap-4">
          <img src={videoIcon} className="w-5 h-5" alt="Video call" />
          <img src={phoneIcon} className="w-5 h-5" alt="Voice call" />
          <img src={verticalDot} className="w-5 h-5" alt="Menu" />
        </div>
      </div>

      {/* Chat Area */}
      <div
        className="min-h-[360px] p-4 bg-cover bg-center flex justify-start"
        style={{ backgroundImage: `url(${whtsappBg})` }}
      >
        <div className="flex flex-col items-start max-w-[80%] w-full">

          {/* Message Bubble */}
          <div className="bg-[#E4FEE0] text-black text-sm p-3 border rounded-2xl w-full">

            {renderHeader()}

            <div className="whitespace-pre-wrap break-words">
              {templateData.bodyText ? (
                fillVariables(templateData.bodyText, bodyParams)
              ) : (
                <span className="text-gray-400 italic">
                  Your message body will appear here...
                </span>
              )}
            </div>

            {templateData.footerText && (
              <div className="mt-2 text-xs text-gray-500 font-normal">
                {templateData.footerText}
              </div>
            )}

            {renderCallToActionButtons()}
          </div>

          {renderQuickReplyButtons()}

          <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-500">
            <span>
              {new Date().toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            <img src={doubleTick} className="w-3 h-3" alt="Delivered" />
          </div>

        </div>
      </div>
    </div>
  );
}